import React, {
  forwardRef,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { resizeNavBar, resizeRightSidebar } from '@repo/redux/actions/ui';
import { selectNavBarWidth, selectRightSidebarWidth } from '@repo/redux/reducers/ui/panelSizing';
import styles from './PanelResizer.module.scss';

const PanelResizer = forwardRef(
  (
    {
      className,
      width,
      minWidth,
      maxWidth,
      updateWidth,
      newWidthFromResizeDelta,
      defaultInitialX,
      afterResize,
    }: {
      className: string;
      width: number;
      minWidth: number;
      maxWidth: number;
      updateWidth: (width: number) => void;
      newWidthFromResizeDelta: (width: number, delta: number) => number;
      defaultInitialX?: number;
      afterResize?: () => void;
    },
    ref: MutableRefObject<HTMLDivElement>,
  ) => {
    const [resizeInProgress, setResizeInProgress] = useState(false);
    const [initialX, setInitialX] = useState(defaultInitialX);

    const handleMouseDown = useCallback((e) => {
      setResizeInProgress(true);
      setInitialX(e.clientX);
    }, []);

    const handleMouseUp = useCallback(() => {
      setResizeInProgress(false);
      afterResize?.();
    }, []);

    useEffect(() => {
      if (resizeInProgress) {
        const handleMouseMove = (e) => {
          const newWidth = Math.max(
            minWidth,
            Math.min(maxWidth, newWidthFromResizeDelta(width, e.clientX - initialX)),
          );
          updateWidth(newWidth);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
          window.removeEventListener('mousemove', handleMouseMove);
          window.removeEventListener('mouseup', handleMouseUp);
        };
      }

      return () => {};
    }, [resizeInProgress, initialX]);

    useEffect(() => {
      if (resizeInProgress) {
        document.body.classList.add('resizing-panel');
      } else {
        document.body.classList.remove('resizing-panel');
      }
    }, [resizeInProgress]);

    return (
      <div
        ref={ref}
        className={cn(styles.resizer, className, resizeInProgress && styles.resizing)}
        onMouseDown={handleMouseDown}
      />
    );
  },
);

export const RightSidebarResizer = () => {
  const dispatch = useDispatch();
  const rightSidebarWidth = useSelector(selectRightSidebarWidth);
  const resizerRef = useRef<HTMLDivElement>(null);

  const afterResize = () => {
    if (resizerRef.current) {
      const constrainedWidth =
        window.outerWidth - resizerRef.current.getBoundingClientRect().left - 2;
      const newWidth = Math.max(300, Math.min(800, constrainedWidth));
      dispatch(resizeRightSidebar(newWidth));
    }
  };

  return (
    <PanelResizer
      ref={resizerRef}
      className={styles.rightSidebarResizer}
      width={rightSidebarWidth}
      minWidth={300}
      maxWidth={800}
      updateWidth={(width) => dispatch(resizeRightSidebar(width))}
      newWidthFromResizeDelta={(width, delta) => width - delta}
      afterResize={afterResize}
    />
  );
};

export const NavBarResizer = () => {
  const dispatch = useDispatch();
  const navBarWidth = useSelector(selectNavBarWidth);

  return (
    <PanelResizer
      className={styles.navBarResizer}
      width={navBarWidth}
      minWidth={200}
      maxWidth={400}
      updateWidth={(width) => dispatch(resizeNavBar(width))}
      newWidthFromResizeDelta={(width, delta) => width + delta}
    />
  );
};
